/*
  WineHive Hive Builder v2 (Oct 2026)
  Replaces the 2022 compiled builder. No dependencies.

  Three ways to start:
    - Designs: the Hive Collection layouts that passed the desk review
    - My dimensions (default): enter width x height, fill every cell that fits; no size cap
    - Draw your own: click or drag on an empty grid
  Every mode lets the customer add/remove cells, pick a color and orientation,
  then prices the design as the cheapest mix of 5-, 20- and 50-Cell kits
  (+ optional wall hardware) and adds those kits to the cart.

  Geometry follows the spec doc: flat-top cell 4.5 in wide x 3.9 in tall,
  columns every 3.375 in, odd columns sit half a cell lower.
  Config (kit/hardware variant ids + prices) comes from window.HiveKits.
*/
(function () {
  'use strict';

  var CFG = window.HiveKits;
  var root = document.getElementById('hb2');
  if (!CFG || !root) return;

  // ---------- geometry ----------
  var SIDE = 2.25;                 // hex side, inches
  var COL = 1.5 * SIDE;            // 3.375 in between columns
  var ROW = Math.sqrt(3) * SIDE;   // 3.897 in between rows
  var CELL_W = 2 * SIDE;           // 4.5 in
  var MAX_COLS = 160, MAX_ROWS = 70;  // drawing safeguard only (about 540 in x 270 in), not a product limit
  var LB_PER_CELL = 4.5;           // 3.5 lb bottle + 1 lb cell (spec doc design load)

  function cx(c) { return c * COL; }
  function cy(c, r) { return r * ROW + (c % 2 ? ROW / 2 : 0); }
  function key(c, r) { return c + ',' + r; }
  function unkey(k) { var p = k.split(','); return [+p[0], +p[1]]; }
  function neighbours(c, r) {
    return c % 2 === 0
      ? [[c, r - 1], [c, r + 1], [c - 1, r - 1], [c - 1, r], [c + 1, r - 1], [c + 1, r]]
      : [[c, r - 1], [c, r + 1], [c - 1, r], [c - 1, r + 1], [c + 1, r], [c + 1, r + 1]];
  }
  function hexPoints(x, y, s) {
    var pts = [];
    for (var i = 0; i < 6; i++) {
      var a = Math.PI / 180 * (60 * i);
      pts.push((x + s * Math.cos(a)).toFixed(3) + ',' + (y + s * Math.sin(a)).toFixed(3));
    }
    return pts.join(' ');
  }

  // ---------- Hive Collection presets (half-row units per column, from the spec doc) ----------
  var PRESETS = [
    { id: 'bar-top', name: 'The Bar Top', blurb: 'Tabletop · no drilling', freestanding: true,
      cols: [[1, 3], [0, 2], [1, 3], [0, 2], [1, 3]] },
    { id: 'pyramid', name: 'The Pyramid', blurb: 'Tabletop · no drilling', freestanding: true,
      cols: [[4], [3, 5], [0, 2, 4], [1, 3, 5], [0, 2, 4], [3, 5], [4]] },
    { id: 'cluster', name: 'The Cluster', blurb: 'Wall · the classic first hive',
      cols: [[2, 4], [1, 3, 5], [0, 2, 4, 6], [1, 3, 5, 7], [0, 2, 4, 6], [3, 5, 7]] },
    { id: 'bloom', name: 'The Bloom', blurb: 'Wall · a flower of cells',
      cols: [[2, 4], [1, 3, 5, 7, 9], [0, 2, 4, 6, 8, 10], [1, 3, 5, 7, 9], [2, 4]] },
    { id: 'floating-cells', name: 'Floating Cells', blurb: 'Wall · three small hives',
      cols: [[0, 2, 4], [1, 3], [], [], [4, 6, 8], [5, 7], [], [], [0, 2, 4], [1, 3]] },
    { id: 'horizon', name: 'The Horizon', blurb: 'Wall · long and low',
      cols: [[0, 2, 4], [1, 3, 5], [0, 2, 4], [1, 3, 5], [0, 2, 4], [1, 3, 5], [0, 2, 4], [1, 3, 5], [0, 2, 4], [1, 3, 5]] }
  ];
  // Turn half-row columns into board cells at offset (ox, oy), keeping column parity right.
  function presetCells(p, ox, oy) {
    var firstIdx = 0;
    while (!p.cols[firstIdx].length) firstIdx++;
    var k = ((p.cols[firstIdx][0] - firstIdx) % 2 + 2) % 2;
    // board column = i + start, and start must have the same parity as k
    var start = (ox % 2 === k) ? ox : ox + 1;
    var out = [];
    p.cols.forEach(function (hs, i) {
      var c = i + start;
      hs.forEach(function (h) { out.push([c, (h - (c % 2)) / 2 + oy]); });
    });
    return out;
  }

  // ---------- state ----------
  var state = {
    mode: 'wall',
    preset: null,
    board: { cols: 14, rows: 8 },
    cells: {},              // key -> true
    color: 'silver',
    orientation: 'flat',    // flat | pointy (rotated 90°)
    units: 'in',
    hardware: true,
    hwTouched: false,
    wall: { w: 40, h: 24 }
  };

  // ---------- pricing ----------
  var SIZES = [50, 20, 5];
  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function bestKits(cells, color) {
    var kits = CFG.kits[color];
    var units = Math.max(1, Math.ceil(cells / 5));
    var maxUnits = units + 10;
    var best = [{ cost: 0, combo: { 5: 0, 20: 0, 50: 0 } }];
    for (var u = 1; u <= maxUnits; u++) {
      best[u] = null;
      SIZES.forEach(function (size) {
        var k = size / 5;
        if (u >= k && best[u - k] && kits[size]) {
          var cost = best[u - k].cost + kits[size].price;
          if (!best[u] || cost < best[u].cost) {
            var combo = { 5: best[u - k].combo[5], 20: best[u - k].combo[20], 50: best[u - k].combo[50] };
            combo[size] += 1;
            best[u] = { cost: cost, combo: combo };
          }
        }
      });
    }
    var pick = null, pickUnits = 0;
    for (var t = units; t <= maxUnits; t++) {
      if (best[t] && (!pick || best[t].cost < pick.cost)) { pick = best[t]; pickUnits = t; }
    }
    return { combo: pick.combo, cost: pick.cost, included: pickUnits * 5, spare: pickUnits * 5 - cells };
  }
  function hardwareFor(combo, color) {
    var hw = CFG.hardware[color], items = [], cost = 0;
    SIZES.forEach(function (s) {
      if (combo[s] && hw[s]) { items.push({ id: hw[s].id, quantity: combo[s] }); cost += hw[s].price * combo[s]; }
    });
    return { items: items, cost: cost };
  }
  function kitText(combo) {
    var parts = [];
    SIZES.forEach(function (s) { if (combo[s]) parts.push(combo[s] + ' × ' + s + '-Cell'); });
    return parts.join(' + ');
  }

  // ---------- analysis ----------
  function filledList() { return Object.keys(state.cells).map(unkey); }
  function analyse() {
    var list = filledList();
    var n = list.length;
    if (!n) return { n: 0 };
    var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, weak = [];
    list.forEach(function (p) {
      var x = cx(p[0]), y = cy(p[0], p[1]);
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      var count = neighbours(p[0], p[1]).filter(function (q) { return state.cells[key(q[0], q[1])]; }).length;
      if (count < 2 && n > 1) weak.push(key(p[0], p[1]));
    });
    var w = maxX - minX + CELL_W, h = maxY - minY + ROW;
    if (state.orientation === 'pointy') { var t = w; w = h; h = t; }
    return { n: n, w: w, h: h, weak: weak, lb: n * LB_PER_CELL };
  }
  function fmtLen(inches) {
    return state.units === 'cm' ? (inches * 2.54).toFixed(0) + ' cm' : inches.toFixed(1) + ' in';
  }

  // ---------- board setup per mode ----------
  function setBoard(cols, rows) { state.board = { cols: cols, rows: rows }; }
  function loadPreset(id) {
    var p = PRESETS.filter(function (x) { return x.id === id; })[0];
    if (!p) return;
    state.preset = p;
    var cells = presetCells(p, 2, 1);
    var maxC = 0, maxR = 0;
    state.cells = {};
    cells.forEach(function (c) { state.cells[key(c[0], c[1])] = true; maxC = Math.max(maxC, c[0]); maxR = Math.max(maxR, c[1]); });
    setBoard(maxC + 3, maxR + 3);
    if (!state.hwTouched) state.hardware = !p.freestanding;
  }
  function fitWall() {
    var w = state.wall.w, h = state.wall.h;
    if (state.units === 'cm') { w = w / 2.54; h = h / 2.54; }
    if (state.orientation === 'pointy') { var t = w; w = h; h = t; }
    var cols = Math.floor((w - CELL_W) / COL) + 1;
    var rowsEven = Math.floor(h / ROW);
    var rowsOdd = Math.floor((h - ROW / 2) / ROW);
    cols = Math.max(0, Math.min(cols, MAX_COLS));
    state.cells = {};
    for (var c = 0; c < cols; c++) {
      var rows = c % 2 ? rowsOdd : rowsEven;
      for (var r = 0; r < Math.min(rows, MAX_ROWS); r++) state.cells[key(c, r)] = true;
    }
    setBoard(Math.max(cols, 1), Math.max(rowsEven, 1));
    state.preset = null;
    if (!state.hwTouched) state.hardware = true;
  }
  function blankBoard() {
    state.cells = {};
    state.preset = null;
    setBoard(14, 8);
    if (!state.hwTouched) state.hardware = true;
  }

  // ---------- rendering ----------
  var el = {};
  function $(sel) { return root.querySelector(sel); }

  var COLORS = {
    silver: { fill: 'url(#hb2-silver)', stroke: '#7d8085', dot: '#7a3b4a' },
    black: { fill: 'url(#hb2-black)', stroke: '#0c0c0c', dot: '#b08a3e' }
  };

  function renderBoard() {
    var svg = el.svg;
    var cols = state.board.cols, rows = state.board.rows;
    var pad = SIDE + 0.6;
    var minX = -pad, minY = -ROW / 2 - 0.6;
    var width = (cols - 1) * COL + 2 * pad;
    var height = rows * ROW + ROW / 2 + 1.2;
    var vb = state.orientation === 'pointy'
      ? [-(height + minY), minX, height, width]
      : [minX, minY, width, height];
    svg.setAttribute('viewBox', vb.join(' '));
    var g = el.grid;
    g.setAttribute('transform', state.orientation === 'pointy' ? 'rotate(90)' : '');
    var a = analyse();
    var weak = {};
    (a.weak || []).forEach(function (k) { weak[k] = true; });
    var color = COLORS[state.color];
    var html = '';
    for (var c = 0; c < cols; c++) {
      for (var r = 0; r < rows; r++) {
        var x = cx(c), y = cy(c, r), k = key(c, r);
        var on = !!state.cells[k];
        html += '<g class="hb2-cell' + (on ? ' is-on' : '') + (weak[k] ? ' is-weak' : '') + '" data-k="' + k + '">' +
          '<polygon points="' + hexPoints(x, y, SIDE * 0.985) + '"' +
          (on ? ' style="fill:' + color.fill + ';stroke:' + color.stroke + '"' : '') + '/>' +
          (on ? '<circle cx="' + x.toFixed(3) + '" cy="' + y.toFixed(3) + '" r="1.05" fill="' + color.dot + '"/>' : '') +
          '</g>';
      }
    }
    g.innerHTML = html;
  }

  function renderSummary() {
    var a = analyse();
    var s = el.summary;
    if (!a.n) {
      s.innerHTML = '<p class="hb2-empty">Pick a design, fit your wall, or click the grid to start your hive.</p>';
      el.cta.disabled = true;
      el.dl.disabled = true;
      el.price.textContent = '';
      return;
    }
    var kits = bestKits(a.n, state.color);
    var hwAll = hardwareFor(kits.combo, state.color);
    var hw = state.hardware ? hwAll : { items: [], cost: 0 };
    var total = kits.cost + hw.cost;
    var notes = [];
    if (a.weak.length) notes.push('<li class="is-warn">' + a.weak.length + ' cell' + (a.weak.length > 1 ? 's hang' : ' hangs') + ' by one joint (outlined in red). Add a neighbour or remove ' + (a.weak.length > 1 ? 'them' : 'it') + '.</li>');
    if (a.h > 36) notes.push('<li>Taller than 3 ft: mount with wall brackets into studs.</li>');
    if (a.n >= 100) notes.push('<li>Large wall: plan for stud mounting. <a href="' + CFG.quoteUrl + '">Ask us about trade pricing and install help</a>.</li>');
    if (state.color === 'black' && (kits.combo[5] || kits.combo[20])) notes.push('<li>Black 5- and 20-Cell kits are low in stock right now and may take longer to ship than silver.</li>');
    if (state.preset && state.preset.freestanding) notes.push('<li>' + state.preset.name + ' is freestanding: no drilling or hardware needed.</li>');

    s.innerHTML =
      '<dl class="hb2-stats">' +
        '<div><dt>Cells</dt><dd>' + a.n + '</dd></div>' +
        '<div><dt>Bottles</dt><dd>' + a.n + '</dd></div>' +
        '<div><dt>Weight full</dt><dd>~' + Math.round(a.lb) + ' lb</dd></div>' +
        '<div class="is-dim"><dt>Max width</dt><dd>' + fmtLen(a.w) + '</dd></div>' +
        '<div class="is-dim"><dt>Max height</dt><dd>' + fmtLen(a.h) + '</dd></div>' +
      '</dl>' +
      '<p class="hb2-kits"><strong>Ships as ' + kitText(kits.combo) + '</strong> <span>' + money(kits.cost) + '</span></p>' +
      (kits.spare > 0 ? '<p class="hb2-spare">Your kits include ' + kits.included + ' cells. Add ' + kits.spare + ' more to the design, or keep ' + (kits.spare === 1 ? 'it as a spare' : 'them as spares') + ' to grow your hive later.</p>' : '') +
      '<label class="hb2-hw"><input type="checkbox" id="hb2-hw"' + (state.hardware ? ' checked' : '') + '> Wall-mount hardware <span>' + money(hwAll.cost) + '</span></label>' +
      (notes.length ? '<ul class="hb2-notes">' + notes.join('') + '</ul>' : '');
    el.price.textContent = money(total);
    el.cta.disabled = false;
    el.dl.disabled = false;
    s.querySelector('#hb2-hw').addEventListener('change', function (e) {
      state.hardware = e.target.checked; state.hwTouched = true; renderSummary();
    });
  }

  function renderModeUI() {
    root.querySelectorAll('[data-mode]').forEach(function (b) {
      var on = b.getAttribute('data-mode') === state.mode;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    root.querySelectorAll('[data-panel]').forEach(function (p) {
      p.hidden = p.getAttribute('data-panel') !== state.mode;
    });
    root.querySelectorAll('[data-preset]').forEach(function (b) {
      b.classList.toggle('is-active', !!state.preset && state.preset.id === b.getAttribute('data-preset'));
    });
    root.querySelectorAll('[data-color]').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-color') === state.color);
      b.setAttribute('aria-pressed', b.getAttribute('data-color') === state.color ? 'true' : 'false');
    });
    root.querySelectorAll('[data-orient]').forEach(function (b) {
      var on = b.getAttribute('data-orient') === state.orientation;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    root.querySelectorAll('[data-units]').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-units') === state.units);
    });
    el.title.textContent = state.preset ? state.preset.name : (state.mode === 'wall' ? 'Fitted to your wall' : 'Your custom hive');
  }

  function render() { renderModeUI(); renderBoard(); renderSummary(); }

  // ---------- preset thumbnails ----------
  function thumbSVG(p) {
    var cells = presetCells(p, 0, 0), minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, poly = '';
    cells.forEach(function (c) {
      var x = cx(c[0]), y = cy(c[0], c[1]);
      minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      poly += '<polygon points="' + hexPoints(x, y, SIDE * 0.95) + '"/>';
    });
    var vb = [minX - SIDE - 0.5, minY - ROW / 2 - 0.5, maxX - minX + 2 * SIDE + 1, maxY - minY + ROW + 1];
    return '<svg viewBox="' + vb.join(' ') + '" aria-hidden="true" focusable="false">' + poly + '</svg>';
  }

  // ---------- interactions ----------
  var painting = null; // 'add' | 'remove' | null
  function cellFromEvent(e) {
    var t = e.target.closest ? e.target.closest('.hb2-cell') : null;
    return t ? t.getAttribute('data-k') : null;
  }
  function applyPaint(k) {
    if (!k) return;
    var on = !!state.cells[k];
    if (painting === 'add' && !on) state.cells[k] = true;
    else if (painting === 'remove' && on) delete state.cells[k];
    else return;
    renderBoard(); renderSummary();
  }
  function onDown(e) {
    var k = cellFromEvent(e);
    if (!k) return;
    e.preventDefault();
    painting = state.cells[k] ? 'remove' : 'add';
    applyPaint(k);
  }
  function onMove(e) {
    if (!painting) return;
    var pt = e.touches ? e.touches[0] : e;
    var target = document.elementFromPoint(pt.clientX, pt.clientY);
    if (!target) return;
    var g = target.closest && target.closest('.hb2-cell');
    if (g && el.svg.contains(g)) { e.preventDefault(); applyPaint(g.getAttribute('data-k')); }
  }
  function onUp() { painting = null; }

  function growBoard() {
    setBoard(Math.min(state.board.cols + 2, MAX_COLS), Math.min(state.board.rows + 1, MAX_ROWS));
    renderBoard();
  }

  // ---------- download ----------
  function download() {
    var a = analyse();
    if (!a.n) return;
    var kits = bestKits(a.n, state.color);
    var list = filledList();
    var scale = 22, padIn = 3;
    var pts = list.map(function (p) {
      var x = cx(p[0]), y = cy(p[0], p[1]);
      return state.orientation === 'pointy' ? [-y, x] : [x, y];
    });
    var minX = Math.min.apply(null, pts.map(function (p) { return p[0]; }));
    var minY = Math.min.apply(null, pts.map(function (p) { return p[1]; }));
    var maxX = Math.max.apply(null, pts.map(function (p) { return p[0]; }));
    var maxY = Math.max.apply(null, pts.map(function (p) { return p[1]; }));
    var head = 150;
    var W = Math.max(720, (maxX - minX + 2 * SIDE + 2 * padIn) * scale);
    var foot = 104;
    var H = head + (maxY - minY + 2 * SIDE + 2 * padIn) * scale + foot;
    var cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    var ctx = cv.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#111'; ctx.textAlign = 'center';
    ctx.font = '600 30px system-ui, -apple-system, Segoe UI, sans-serif';
    ctx.fillText('WineHive' + (state.preset ? ' · ' + state.preset.name : ' · Custom Hive'), W / 2, 50);
    ctx.font = '18px system-ui, -apple-system, Segoe UI, sans-serif';
    ctx.fillStyle = '#444';
    ctx.fillText(a.n + ' cells · max ' + fmtLen(a.w) + ' wide × ' + fmtLen(a.h) + ' tall · ' + (state.color === 'black' ? 'Black' : 'Silver') + ' · ' + (state.orientation === 'pointy' ? 'Rotated' : 'Flat') + ' · ' + el.price.textContent, W / 2, 86);
    ctx.fillText(kitText(kits.combo) + ' · ' + new Date().toLocaleDateString(), W / 2, 114);
    var offX = (W - (maxX - minX) * scale) / 2, offY = head + (padIn + SIDE) * scale;
    pts.forEach(function (p) {
      var x = offX + (p[0] - minX) * scale, y = offY + (p[1] - minY) * scale, s = SIDE * 0.97 * scale;
      ctx.beginPath();
      for (var i = 0; i < 6; i++) {
        var ang = Math.PI / 180 * (60 * i + (state.orientation === 'pointy' ? 30 : 0));
        var px = x + s * Math.cos(ang), py = y + s * Math.sin(ang);
        if (i) ctx.lineTo(px, py); else ctx.moveTo(px, py);
      }
      ctx.closePath();
      ctx.fillStyle = state.color === 'black' ? '#2b2b2b' : '#dcdee1'; ctx.fill();
      ctx.lineWidth = 2; ctx.strokeStyle = state.color === 'black' ? '#000' : '#7d8085'; ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, 1.05 * scale, 0, Math.PI * 2);
      ctx.fillStyle = state.color === 'black' ? '#b08a3e' : '#7a3b4a'; ctx.fill();
    });
    // footer call to action
    ctx.fillStyle = '#be003b'; ctx.fillRect(0, H - foot, W, foot);
    ctx.fillStyle = '#ffffff'; ctx.font = '700 22px system-ui, -apple-system, Segoe UI, sans-serif';
    ctx.fillText('Questions about your design? Email info@winehive.com', W / 2, H - foot + 44);
    ctx.font = '16px system-ui, -apple-system, Segoe UI, sans-serif'; ctx.fillStyle = 'rgba(255,255,255,.85)';
    ctx.fillText('Order it at winehive.com/pages/hive-builder', W / 2, H - foot + 76);
    var link = document.createElement('a');
    link.download = 'winehive-' + (state.preset ? state.preset.id : 'custom') + '-' + a.n + '-cells.png';
    link.href = cv.toDataURL('image/png');
    document.body.appendChild(link); link.click(); link.remove();
  }

  // ---------- cart ----------
  function addToCart() {
    var a = analyse();
    if (!a.n) return;
    var kits = bestKits(a.n, state.color);
    var hw = state.hardware ? hardwareFor(kits.combo, state.color) : { items: [], cost: 0 };
    var label = (state.preset ? state.preset.name : 'Custom hive') + ' · ' + a.n + ' cells · max ' + fmtLen(a.w) + ' W × ' + fmtLen(a.h) + ' H · ' + (state.orientation === 'pointy' ? 'rotated cells' : 'flat cells');
    var items = [];
    SIZES.forEach(function (s) {
      if (kits.combo[s]) items.push({ id: CFG.kits[state.color][s].id, quantity: kits.combo[s], properties: { 'Hive Builder design': label } });
    });
    items = items.concat(hw.items);
    el.cta.disabled = true;
    el.cta.classList.add('is-loading');
    el.err.hidden = true;
    fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      return res.json().then(function (data) { return { ok: res.ok, data: data }; });
    }).then(function (r) {
      if (!r.ok) throw new Error((r.data && (r.data.description || r.data.message)) || 'Could not add to cart');
      window.location.href = CFG.afterAdd || '/cart';
    }).catch(function (e) {
      el.err.textContent = e.message + '. Please try again or contact us.';
      el.err.hidden = false;
      el.cta.disabled = false;
      el.cta.classList.remove('is-loading');
    });
  }

  // ---------- init ----------
  function init() {
    el.svg = $('.hb2-board svg');
    el.grid = $('.hb2-grid');
    el.summary = $('.hb2-summary');
    el.price = $('.hb2-price');
    el.cta = $('.hb2-cta');
    el.dl = $('.hb2-download');
    el.err = $('.hb2-error');
    el.title = $('.hb2-board-title');

    var list = $('.hb2-presets');
    list.innerHTML = PRESETS.map(function (p) {
      var n = presetCells(p, 0, 0).length;
      return '<button type="button" class="hb2-preset" data-preset="' + p.id + '">' + thumbSVG(p) +
        '<span class="hb2-preset-name">' + p.name + '</span>' +
        '<span class="hb2-preset-meta">' + n + ' cells · ' + p.blurb + '</span></button>';
    }).join('');

    root.addEventListener('click', function (e) {
      var t = e.target.closest('button, [data-mode]');
      if (!t || !root.contains(t)) return;
      if (t.hasAttribute('data-mode')) {
        state.mode = t.getAttribute('data-mode');
        if (state.mode === 'designs' && !state.preset) loadPreset('cluster');
        if (state.mode === 'wall') fitWall();
        if (state.mode === 'draw') blankBoard();
        render();
      } else if (t.hasAttribute('data-preset')) {
        loadPreset(t.getAttribute('data-preset')); render();
      } else if (t.hasAttribute('data-color')) {
        state.color = t.getAttribute('data-color'); render();
      } else if (t.hasAttribute('data-orient')) {
        state.orientation = t.getAttribute('data-orient');
        if (state.mode === 'wall') fitWall();
        render();
      } else if (t.hasAttribute('data-units')) {
        var u = t.getAttribute('data-units');
        if (u !== state.units) {
          var f = u === 'cm' ? 2.54 : 1 / 2.54;
          state.wall.w = Math.round(state.wall.w * f); state.wall.h = Math.round(state.wall.h * f);
          el.wallW.value = state.wall.w; el.wallH.value = state.wall.h;
          state.units = u;
        }
        render();
      } else if (t.classList.contains('hb2-fit')) {
        state.wall.w = parseFloat(el.wallW.value) || 0; state.wall.h = parseFloat(el.wallH.value) || 0;
        var minV = state.units === 'cm' ? 12 : 5;
        if (state.wall.w < minV || state.wall.h < minV) {
          el.wallMsg.textContent = 'Width and height need to be at least ' + minV + ' ' + state.units + '.';
          return;
        }
        var inW = state.units === 'cm' ? state.wall.w / 2.54 : state.wall.w, inH = state.units === 'cm' ? state.wall.h / 2.54 : state.wall.h;
        if (inW > 540 || inH > 270) {
          el.wallMsg.innerHTML = 'That is a grand hive. We have drawn the first 540 in &times; 270 in here; <a href="' + (CFG.quoteUrl || '/pages/contact') + '">send us your full dimensions</a> and our team will plan the rest with you.';
        } else {
          el.wallMsg.textContent = '';
        }
        fitWall(); render();
      } else if (t.classList.contains('hb2-clear')) {
        state.cells = {}; state.preset = null; render();
      } else if (t.classList.contains('hb2-grow')) {
        growBoard();
      } else if (t.classList.contains('hb2-cta')) {
        addToCart();
      } else if (t.classList.contains('hb2-download')) {
        download();
      }
    });

    el.wallW = $('#hb2-wall-w'); el.wallH = $('#hb2-wall-h'); el.wallMsg = $('.hb2-wall-msg');
    el.wallW.value = state.wall.w; el.wallH.value = state.wall.h;

    el.svg.addEventListener('mousedown', onDown);
    el.svg.addEventListener('touchstart', onDown, { passive: false });
    window.addEventListener('mousemove', onMove);
    el.svg.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);

    // deep links: ?design=bloom | ?mode=wall | ?mode=draw | ?color=black
    var q = new URLSearchParams(window.location.search);
    if (q.get('color') === 'black') state.color = 'black';
    var m = q.get('mode');
    if (m === 'draw') { state.mode = 'draw'; blankBoard(); }
    else if (m === 'designs' || q.get('design')) { state.mode = 'designs'; loadPreset(q.get('design') || 'cluster'); }
    else { state.mode = 'wall'; fitWall(); }   // default: start from the customer's own wall size
    render();
    root.classList.add('is-ready');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
